import { useState } from "react";
import { useIntl } from "react-intl";

import { useGetTrackingUpdates } from "api/queries/shipments/useGetTrackingUpdates";
import { extractAllFilesFromMessages } from "helpers/trackingHelpers";
import useGetDefaultFiles from "../components/Activity/components/ActivityFiles/useGetDefaultFiles";
import ActivityField from "../components/Activity/components/ActivityField/ActivityField";
import ActivityList from "../components/Activity/components/ActivityList/ActivityList";
import ActivityFiles from "../components/Activity/components/ActivityFiles/ActivityFiles";

export type ActivityActionOptions = {
  /**
   * The shipment ID.
   */
  shipmentId: string;
  /**
   * The shipment details data from getShipmentData.
   */
  shipmentDetails: any;
  /**
   * Whether the shipment is off-platform.
   */
  isOffPlatform: boolean;
};

export default function useActivityActions({ shipmentId, shipmentDetails, isOffPlatform }: ActivityActionOptions) {
  const intl = useIntl();
  const [actKey, setActKey] = useState("all");

  const { data: trackingUpdatesData, isLoading: isLoadingTrackingUpdates } = useGetTrackingUpdates(shipmentId, {
    enabled: !!shipmentId,
  });
  const { booking_date } = shipmentDetails?.schedule ?? {};
  const defaultFilesData = useGetDefaultFiles({ shipmentId, booking_date });
  const filesExtractedFromMessages = extractAllFilesFromMessages(trackingUpdatesData?.updates);

  const handleTabSelect = (key: string) => {
    setActKey(key);
  };

  const tabs = [
    {
      key: "all",
      title: intl.formatMessage({ id: "general.viewAll", defaultMessage: "View all" }),
      content: (
        <>
          <ActivityField shipmentId={shipmentId} />
          <ActivityList
            trackingUpdatesData={trackingUpdatesData}
            shipmentDetails={shipmentDetails}
            defaultFilesData={isOffPlatform ? undefined : defaultFilesData}
            filesExtractedFromMessages={filesExtractedFromMessages}
          />
        </>
      ),
    },
    {
      key: "comments",
      title: intl.formatMessage({ id: "general.comments", defaultMessage: "Comments" }),
      content: (
        <>
          <ActivityField shipmentId={shipmentId} />
          <ActivityList
            trackingUpdatesData={trackingUpdatesData}
            shipmentDetails={shipmentDetails}
            filesExtractedFromMessages={filesExtractedFromMessages}
            viewCommentsOnly
          />
        </>
      ),
    },
    {
      key: "files",
      title: intl.formatMessage({ id: "general.files", defaultMessage: "Files" }),
      content: (
        <ActivityFiles
          trackingUpdatesData={trackingUpdatesData}
          shipmentId={shipmentId}
          isOffPlatform={isOffPlatform}
          defaultFilesData={isOffPlatform ? undefined : defaultFilesData}
          filesExtractedFromMessages={filesExtractedFromMessages}
        />
      ),
    },
  ];

  return { intl, actKey, tabs, handleTabSelect, isLoadingTrackingUpdates };
}
